import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Modal } from '../ui';

/**
 * The privacy notice shown wherever a guest's details are taken - check-in,
 * online booking, the QR menu, a table reservation - and the consent
 * questions that go with it.
 *
 * A short summary with the full notice one click away, not a wall of legal
 * text in front of the form. Consent checkboxes appear only for purposes a
 * legal decision has marked as needing consent (Platform > Privacy >
 * Purposes); while none is decided, there are none, and nothing is presumed
 * agreed. A checkbox starts unticked: silence is never consent.
 */

export interface CollectionConfig {
  /** ENCRYPTED, LAST4_ONLY or NONE - how much of an ID number the property keeps. */
  guestIdStorage?: 'ENCRYPTED' | 'LAST4_ONLY' | 'NONE';
  notice: { id: string; version: string; title: string; summary: string; content: string } | null;
  consentPurposes: Array<{ purpose: string; label: string; description: string }>;
}

export type ConsentChoices = Record<string, boolean>;

/** What the form sends in its `privacy` field, or nothing when there is nothing to say. */
export function privacyPayload(config: CollectionConfig | undefined, choices: ConsentChoices) {
  if (!config) return undefined;
  const consents = config.consentPurposes.map((purpose) => ({
    purpose: purpose.purpose,
    granted: Boolean(choices[purpose.purpose]),
  }));
  if (!config.notice && consents.length === 0) return undefined;
  return {
    ...(config.notice ? { noticeId: config.notice.id } : {}),
    ...(consents.length > 0 ? { consents } : {}),
  };
}

/** Staff forms read the signed-in property's notice; public pages read it by menu token. */
export function useCollectionConfig(menuToken?: string) {
  return useQuery({
    queryKey: ['privacy-collection', menuToken ?? 'staff'],
    queryFn: () =>
      api.get<CollectionConfig>(
        menuToken ? `/api/public/menu/${menuToken}/privacy-notice` : '/api/privacy/collection',
      ),
    staleTime: 5 * 60_000,
  });
}

export function GuestPrivacyNotice({
  config,
  choices,
  onChange,
  audience = 'guest',
}: {
  config: CollectionConfig | undefined;
  choices: ConsentChoices;
  onChange: (next: ConsentChoices) => void;
  /** Staff read the notice to the guest; a guest reads it themselves. */
  audience?: 'guest' | 'staff';
}) {
  const [open, setOpen] = useState(false);
  if (!config || (!config.notice && config.consentPurposes.length === 0)) return null;

  return (
    <div className="privacy-notice">
      {config.notice ? (
        <p className="small muted" style={{ margin: 0 }}>
          {audience === 'staff' ? 'Tell the guest: ' : ''}
          {config.notice.summary}{' '}
          <button type="button" className="linklike" onClick={() => setOpen(true)}>
            Read the full privacy notice
          </button>
        </p>
      ) : null}

      {config.consentPurposes.map((purpose) => (
        <label key={purpose.purpose} className="row gap-6 small" style={{ alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            checked={Boolean(choices[purpose.purpose])}
            onChange={(event) => onChange({ ...choices, [purpose.purpose]: event.target.checked })}
          />
          <span>
            <b>{purpose.label}</b> - {purpose.description}
            {audience === 'staff' ? <span className="muted"> (only if the guest agrees)</span> : null}
          </span>
        </label>
      ))}

      {open && config.notice ? (
        <Modal title={config.notice.title} onClose={() => setOpen(false)}>
          <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
            {config.notice.content}
          </div>
          <p className="xs muted">Version {config.notice.version}</p>
        </Modal>
      ) : null}
    </div>
  );
}
